/**
 * 🎬 ANIMATION VARIANTS 2025 - Framer Motion
 * 
 * Zentrale Animation-Presets für alle Elemente
 * Ersetzt alte CSS-basierte Animationen mit eleganten, performanten Framer Motion Animationen
 * 
 * Features:
 * - Smooth, natural animations mit Spring-Physics
 * - Scroll-triggered animations via whileInView
 * - Stagger animations für mehrere Elemente
 * - Performance-optimiert (Hardware-accelerated)
 * - Einfach wiederverwendbar
 */

// 🎨 EASING FUNCTIONS - Natural, elegant easing curves
export const easings = {
    smooth: [0.25, 0.1, 0.25, 1], // Standard smooth
    elegant: [0.16, 1, 0.3, 1], // Luxury, premium feel
    bounce: [0.68, -0.55, 0.265, 1.55], // Playful bounce
    swift: [0.4, 0, 0.2, 1] // Fast & snappy
};

// ⚡ SPRING CONFIGURATIONS - For natural, physics-based motion
export const springs = {
    gentle: {
        type: "spring",
        stiffness: 100,
        damping: 20,
        mass: 1
    },
    snappy: {
        type: "spring",
        stiffness: 300,
        damping: 30,
        mass: 0.5
    },
    bouncy: {
        type: "spring",
        stiffness: 400,
        damping: 10,
        mass: 0.8
    }
};

// 🎯 ANIMATION VARIANTS - Pre-defined animation presets

/**
 * Fade In Up - Classic smooth entrance from bottom
 */
export const fadeInUp = {
    hidden: {
        opacity: 0,
        y: 30,
        scale: 0.98
    },
    visible: {
        opacity: 1,
        y: 0,
        scale: 1,
        transition: {
            duration: 0.6,
            ease: easings.elegant
        }
    }
};

/**
 * Slide In Left - Entrance from left side
 */
export const slideInLeft = {
    hidden: {
        opacity: 0,
        x: -50
    },
    visible: {
        opacity: 1,
        x: 0,
        transition: {
            duration: 0.6,
            ease: easings.smooth
        }
    }
};

/**
 * Slide In Right - Entrance from right side
 */
export const slideInRight = {
    hidden: {
        opacity: 0,
        x: 50
    },
    visible: {
        opacity: 1,
        x: 0,
        transition: {
            duration: 0.6,
            ease: easings.smooth
        }
    }
};

/**
 * Fade In - Simple, elegant fade
 */
export const fadeIn = {
    hidden: {
        opacity: 0
    },
    visible: {
        opacity: 1,
        transition: {
            duration: 0.5,
            ease: easings.smooth
        }
    }
};

/**
 * Elegant Scale - Premium, sophisticated scale + fade
 */
export const elegantScale = {
    hidden: {
        opacity: 0,
        scale: 0.92,
        y: 8,
        filter: "blur(4px)"
    },
    visible: {
        opacity: 1,
        scale: 1,
        y: 0,
        filter: "blur(0px)",
        transition: {
            duration: 0.8,
            ease: easings.elegant
        }
    }
};

/**
 * Scale Pop - Playful, attention-grabbing entrance
 */
export const scalePop = {
    hidden: {
        opacity: 0,
        scale: 0.8
    },
    visible: {
        opacity: 1,
        scale: 1,
        transition: springs.bouncy
    }
};

/**
 * Blur In - Modern blur effect entrance
 */
export const blurIn = {
    hidden: {
        opacity: 0,
        filter: "blur(10px)"
    },
    visible: {
        opacity: 1,
        filter: "blur(0px)",
        transition: {
            duration: 0.7,
            ease: easings.smooth
        }
    }
};

/**
 * Slide Up - Minimal, clean upward motion
 */
export const slideUp = {
    hidden: {
        y: 20,
        opacity: 0
    },
    visible: {
        y: 0,
        opacity: 1,
        transition: {
            duration: 0.5,
            ease: easings.smooth
        }
    }
};

/**
 * Rotate In - Playful rotation entrance
 */
export const rotateIn = {
    hidden: {
        opacity: 0,
        rotate: -5,
        scale: 0.95
    },
    visible: {
        opacity: 1,
        rotate: 0,
        scale: 1,
        transition: {
            duration: 0.6,
            ease: easings.elegant
        }
    }
};

// 📋 ALL VARIANTS MAP - Easy access to all animation presets
export const animationVariants = {
    fadeInUp,
    slideInLeft,
    slideInRight,
    fadeIn,
    elegantScale,
    scalePop,
    blurIn,
    slideUp,
    rotateIn
};

// 🎯 UTILITY FUNCTION: Get variant by name
export const getVariant = (variantName) => {
    return animationVariants[variantName] || fadeInUp; // Fallback to fadeInUp
};

// ⏱️ DURATION MAPPINGS - Convert speed to actual duration
export const getDuration = (speed = 'normal') => {
    const durations = {
        fast: 0.4,
        normal: 0.6,
        slow: 1.0
    };
    return durations[speed] || durations.normal;
};

// 🎬 APPLY CUSTOM DURATION - Modify variant duration
export const withCustomDuration = (variant, speed = 'normal') => {
    const duration = getDuration(speed);
    
    return {
        ...variant,
        visible: {
            ...variant.visible,
            transition: {
                ...variant.visible.transition,
                duration
            }
        }
    };
};

// 🎯 VIEWPORT CONFIG - When to trigger animation
export const viewportConfig = {
    once: true, // ✅ Only animate once (not on every scroll)
    amount: 0.1, // ✅ Trigger when 10% of element is visible
    margin: "0px 0px -100px 0px" // ✅ Trigger 100px before element enters viewport
};

// 🎯 VIEWPORT CONFIG (Images) - Images need more lead time
export const viewportConfigImages = {
    once: true,
    amount: 0.1,
    margin: "0px 0px -150px 0px" // ✅ Trigger 150px before (more lead time for images)
};

// 🎭 STAGGER CONTAINER - For animating multiple children with delay
export const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
        opacity: 1,
        transition: {
            staggerChildren: 0.1, // 100ms delay between children
            delayChildren: 0.1 // Wait 100ms before starting
        }
    }
};

// 🎭 STAGGER ITEM - Child item for stagger animations
export const staggerItem = {
    hidden: {
        opacity: 0,
        y: 20
    },
    visible: {
        opacity: 1,
        y: 0,
        transition: {
            duration: 0.5,
            ease: easings.smooth
        }
    }
};

export default animationVariants;

